import React, {memo, useMemo} from 'react';
import {maskPhoneNumber} from '../../../../utils/phoneUtils';
import {DeliveryTaskOrderType} from '../../graphql/types/DeliveryTaskOrderType';
import {ExceptionDictionaryType, ExceptionSubReasonType} from '../../graphql/queries/getExceptionDictionaryForDelivery';
import OptimizedOrderItem from './OptimizedOrderItem';

interface OrderGroup {
    fullName: string;
    phone: string;
    address: string;
    orders: DeliveryTaskOrderType[];
}

interface OptimizedOrderGroupProps {
    group: OrderGroup;
    isExpanded: boolean;
    maxDisplay: number;
    expandedOrders: Set<number>;
    exceptionReason?: ExceptionDictionaryType;
    exceptionSubReasons?: ExceptionSubReasonType[];
    displayedCountRef: {current: number};
    onToggleOrder: (orderId: number) => void;
}

const OptimizedOrderGroup: React.FC<OptimizedOrderGroupProps> = memo(({
    group,
    isExpanded,
    maxDisplay,
    expandedOrders,
    exceptionReason,
    exceptionSubReasons,
    displayedCountRef,
    onToggleOrder
}) => {
    const groupKey = `${group.fullName}_${group.phone}`;

    // 使用 useMemo 缓存应该显示的订单列表
    const shouldDisplayOrders = useMemo(() => {
        if (isExpanded) return group.orders; // 展开状态显示全部

        const result: DeliveryTaskOrderType[] = [];
        for (const order of group.orders) {
            if (displayedCountRef.current >= maxDisplay) break;
            result.push(order);
            displayedCountRef.current++;
        }
        return result;
    }, [group.orders, isExpanded, maxDisplay, displayedCountRef]);

    // 如果这个分组没有要显示的订单，跳过
    if (shouldDisplayOrders.length === 0) return null;

    return (
        <div key={groupKey} className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* 订单列表 */}
            {shouldDisplayOrders.map((order, orderIndex) => (
                <OptimizedOrderItem
                    key={order.id}
                    order={order}
                    isExpanded={expandedOrders.has(order.id)}
                    isLastItem={orderIndex === shouldDisplayOrders.length - 1}
                    exceptionReason={exceptionReason}
                    exceptionSubReasons={exceptionSubReasons}
                    onToggle={onToggleOrder}
                />
            ))}

            {/* 收件人信息（卡片底部） */}
            <div className="px-4 py-3 flex justify-between items-center border-1">
                <span className="text-sm font-medium">
                    {group.fullName.length > 20
                        ? `${group.fullName.slice(0, 17)}...`
                        : group.fullName}
                </span>
                <span className="text-sm text-gray-400">
                    {maskPhoneNumber(group.phone)}
                </span>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // 只有这些关键属性变化时才重新渲染
    return prevProps.group === nextProps.group &&
        prevProps.isExpanded === nextProps.isExpanded &&
        prevProps.expandedOrders === nextProps.expandedOrders &&
        prevProps.maxDisplay === nextProps.maxDisplay &&
        prevProps.exceptionReason === nextProps.exceptionReason &&
        prevProps.exceptionSubReasons === nextProps.exceptionSubReasons;
});

OptimizedOrderGroup.displayName = 'OptimizedOrderGroup';

export default OptimizedOrderGroup;
